/**
 * 本机节点桥接：把这台设备变成局域网里的会议节点。
 *
 * 用在云端连不上、或者会议本来就在没有外网的教室里的时候。桌面 App 里随包
 * 带着一个完整的节点程序，起来之后同一局域网的其他人用浏览器就能进会。
 *
 * 桌面 App 拉起随包分发的节点程序，iOS App 在自己进程内跑节点；网页版没法监听端口，
 * 安卓还没有节点产物。能不能当由原生侧说了算（capabilities.localNode），界面据此隐藏入口。
 */

import { capabilities, isDesktop } from './desktop'

/** 节点状态。字段与 Rust 侧 NodeStatus 一一对应。 */
export interface NodeStatus {
  running: boolean
  /** 操作系统进程号，任务管理器里能对上 */
  pid?: number | null
  /** 局域网入会地址，念给同事的就是这个 */
  url?: string | null
  room?: string | null
  hostCode?: string | null
  guestCode?: string | null
  /** 失败原因。节点自己退出时（端口被占等）会填在这里 */
  error?: string | null
}

/**
 * 本机能不能当节点 —— 问原生侧，不在前端按平台猜。
 *
 * 以前按 UA 猜「手机一律不能」，原生侧支持 iOS 之后这里不改就永远不显示入口：
 * 功能做完了却没人用得到，而且没有任何报错。平台能力的事实只有 Rust 那边知道。
 */
export async function canHostNode(): Promise<boolean> {
  if (!isDesktop()) return false
  try {
    return (await capabilities()).localNode
  } catch {
    return false
  }
}

async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const t = window.__TAURI__
  if (!t) throw new Error('当前不在桌面客户端中运行')
  return t.core.invoke<T>(cmd, args)
}

/**
 * 启动本机节点。返回启动瞬间的状态 —— 入会码此时可能还没打出来，
 * 要靠 onNodeStatus 等后续推送。
 */
export async function startNode(room?: string): Promise<NodeStatus> {
  if (!(await canHostNode())) throw new Error('这台设备不能当节点，请换用电脑或 iPhone 上的客户端')
  return invoke<NodeStatus>('start_node', { room: room ?? null })
}

/** 停止本机节点。会议里的人会收到正常的关闭通知，不是被硬断。 */
export async function stopNode(): Promise<NodeStatus> {
  if (!(await canHostNode())) return { running: false }
  return invoke<NodeStatus>('stop_node')
}

/** 查询节点状态。 */
export async function nodeStatus(): Promise<NodeStatus> {
  if (!(await canHostNode())) return { running: false }
  return invoke<NodeStatus>('node_status')
}

/**
 * 订阅节点状态变化，返回退订函数。
 *
 * 必须有这条推送而不是只靠轮询：入会码是节点起来一两秒后才打印出来的，
 * 而用户点完按钮就在等那两串码。
 */
export async function onNodeStatus(fn: (s: NodeStatus) => void): Promise<() => void> {
  const t = window.__TAURI__
  if (!t?.event) return () => {}
  return t.event.listen<NodeStatus>('node-status', (e) => fn(e.payload))
}
